import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import { toast } from "react-toastify";
import FolderIcon from "@mui/icons-material/Folder";
import HomeIcon from "@mui/icons-material/Home";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CollectionsApi from "../../services/api/collections";

function CollectionFolderMoveDialog({
  open,
  onClose,
  collectionId,
  currentFolderPath = [],
  collections = [],
  emptyFolders = [],
  token,
  onMoved,
}) {
  const [currentLevel, setCurrentLevel] = useState([]);
  const [newFolderInput, setNewFolderInput] = useState("");
  const [selectedPath, setSelectedPath] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);

  // Derive unique folder paths from all collections and emptyFolders
  const existingFolders = useMemo(() => {
    const folderSet = new Set();
    folderSet.add("[]"); // Root
    const addWithPrefixes = (path) => {
      for (let i = 1; i <= path.length; i++) {
        folderSet.add(JSON.stringify(path.slice(0, i)));
      }
    };
    collections.forEach((c) => {
      if (Array.isArray(c.folderPath) && c.folderPath.length > 0) {
        addWithPrefixes(c.folderPath);
      }
    });
    emptyFolders.forEach((ef) => {
      if (Array.isArray(ef) && ef.length > 0) {
        addWithPrefixes(ef);
      }
    });
    return Array.from(folderSet).map((s) => JSON.parse(s));
  }, [collections, emptyFolders]);

  // Folders directly under currentLevel (exactly one more segment)
  const foldersAtLevel = useMemo(() => {
    const seen = new Set();
    return existingFolders
      .filter((f) => {
        if (f.length !== currentLevel.length + 1) return false;
        for (let i = 0; i < currentLevel.length; i++) {
          if (f[i] !== currentLevel[i]) return false;
        }
        return true;
      })
      .filter((f) => {
        const key = JSON.stringify(f);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [existingFolders, currentLevel]);

  const goUp = () => {
    setCurrentLevel((prev) => prev.slice(0, -1));
    setSelectedPath(null);
    setShowNewFolder(false);
  };

  const drillInto = (folder) => {
    if (currentLevel.length >= 2) return; // Max depth 2
    setCurrentLevel(folder);
    setSelectedPath(null);
    setShowNewFolder(false);
  };

  const selectPath = (path) => {
    setSelectedPath(path);
    setShowNewFolder(false);
  };

  // Parse new folder input (supports "Folder / Subfolder" or "Folder/Subfolder")
  const parseNewFolder = () => {
    if (!newFolderInput.trim()) return [];
    const parsed = newFolderInput
      .split(/\s*\/\s*/)
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 2); // Max 2 levels
    return [...currentLevel, ...parsed].slice(0, 2);
  };

  const handleSave = async () => {
    const folderPath =
      selectedPath !== null
        ? selectedPath
        : showNewFolder
        ? parseNewFolder()
        : currentLevel;

    setSaving(true);
    try {
      await CollectionsApi.updateCollectionFolderPath(token, collectionId, folderPath);
      if (onMoved) onMoved(folderPath);
      onClose();
    } catch (err) {
      console.error("Failed to move collection:", err);
      toast.error("Failed to move collection. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSelectNewFolder = () => {
    setShowNewFolder(true);
    setSelectedPath(null);
  };

  const canGoDeeper = currentLevel.length < 2;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {currentLevel.length > 0 && (
            <IconButton onClick={goUp} size="small" aria-label="Go back">
              <ArrowBackIcon />
            </IconButton>
          )}
          <span>Move Collection to Folder</span>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <List dense>
          {/* Select this folder option */}
          <ListItemButton
            selected={selectedPath !== null && JSON.stringify(selectedPath) === JSON.stringify(currentLevel) && !showNewFolder}
            onClick={() => selectPath(currentLevel)}
          >
            <ListItemIcon>
              <HomeIcon />
            </ListItemIcon>
            <ListItemText
              primary={currentLevel.length === 0 ? "No folder" : "Select this folder"}
              secondary={currentLevel.length === 0 ? "Top-level location" : currentLevel.join(" / ")}
            />
            {selectedPath !== null && JSON.stringify(selectedPath) === JSON.stringify(currentLevel) && !showNewFolder && (
              <CheckIcon color="primary" />
            )}
          </ListItemButton>

          {/* Existing folders at this level */}
          {foldersAtLevel.map((folder, i) => (
            <ListItemButton
              key={i}
              onClick={() => drillInto(folder)}
            >
              <ListItemIcon>
                <FolderIcon />
              </ListItemIcon>
              <ListItemText primary={folder[folder.length - 1]} />
              {canGoDeeper && (
                <ArrowForwardIcon fontSize="small" color="action" />
              )}
            </ListItemButton>
          ))}

          {/* New folder option */}
          <ListItemButton
            selected={showNewFolder}
            onClick={handleSelectNewFolder}
          >
            <ListItemIcon>
              <AddIcon />
            </ListItemIcon>
            <ListItemText primary="New folder..." />
            {showNewFolder && <CheckIcon color="primary" />}
          </ListItemButton>
        </List>

        {showNewFolder && (
          <Box sx={{ mt: 2, px: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Folder path"
              placeholder="Folder / Subfolder"
              value={newFolderInput}
              onChange={(e) => setNewFolderInput(e.target.value)}
              helperText="Use / to separate levels (max 2)"
            />
          </Box>
        )}

        {/* Current path indicator */}
        {currentFolderPath.length > 0 && (
          <Box sx={{ mt: 2, px: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Current: {currentFolderPath.join(" / ")}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Moving..." : "Move"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CollectionFolderMoveDialog;
